'use client';

import DefaultStyle from 'lowcode/common/DefaultStyle';
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';

const getYoutubeId = (url = '') => {
    const value = url.trim();
    if (!value) return '';

    try {
        const parsed = new URL(value);
        const host = parsed.hostname.replace(/^www\./, '');

        if (host === 'youtu.be') {
            return parsed.pathname.split('/').filter(Boolean)[0] || '';
        }

        if (host.includes('youtube.com')) {
            if (parsed.pathname.startsWith('/embed/')) {
                return parsed.pathname.split('/').filter(Boolean)[1] || '';
            }
            if (parsed.pathname.startsWith('/shorts/')) {
                return parsed.pathname.split('/').filter(Boolean)[1] || '';
            }
            return parsed.searchParams.get('v') || '';
        }
    } catch {
        return value;
    }

    return value;
};

let youtubeApiPromise: Promise<void> | null = null;

const loadYoutubeApi = () => {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('YouTube API is unavailable on server'));
    }

    if ((window as any).YT?.Player) {
        return Promise.resolve();
    }

    if (!youtubeApiPromise) {
        youtubeApiPromise = new Promise((resolve) => {
            const previousReady = (window as any).onYouTubeIframeAPIReady;
            (window as any).onYouTubeIframeAPIReady = () => {
                previousReady?.();
                resolve();
            };

            if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
                const script = document.createElement('script');
                script.src = 'https://www.youtube.com/iframe_api';
                script.async = true;
                document.head.appendChild(script);
            }
        });
    }

    return youtubeApiPromise;
};

export default function Content({ id, data, attr = {}, isEditStatus = false }: any) {
    const { videoUrl = '', autoPlay = true, bgColor = '' } = data || {};
    const shouldAutoPlay = autoPlay !== false;
    const videoId = useMemo(() => getYoutubeId(videoUrl), [videoUrl]);
    const containerRef = useRef<HTMLDivElement>(null);
    const playerHostRef = useRef<HTMLDivElement>(null);
    const playerRef = useRef<any>(null);
    const isInViewRef = useRef(false);
    const isReadyRef = useRef(false);
    const [isMounted, setIsMounted] = useState(false);
    const [shouldLoadPlayer, setShouldLoadPlayer] = useState(isEditStatus);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        isReadyRef.current = false;
        playerRef.current?.destroy?.();
        playerRef.current = null;

        if (isEditStatus) {
            setShouldLoadPlayer(true);
            return;
        }

        setShouldLoadPlayer(false);
    }, [videoId, isEditStatus]);

    useEffect(() => {
        if (!isMounted || !shouldLoadPlayer || !videoId || !playerHostRef.current) {
            return;
        }

        let cancelled = false;

        const syncPlayback = () => {
            if (!isReadyRef.current || !playerRef.current) return;

            if ((isEditStatus || isInViewRef.current) && shouldAutoPlay) {
                playerRef.current.playVideo?.();
            } else {
                playerRef.current.pauseVideo?.();
            }
        };

        const initPlayer = async () => {
            try {
                await loadYoutubeApi();
            } catch {
                return;
            }

            if (cancelled || !playerHostRef.current) return;

            playerRef.current?.destroy?.();
            playerRef.current = null;
            isReadyRef.current = false;

            playerRef.current = new (window as any).YT.Player(playerHostRef.current, {
                videoId,
                playerVars: {
                    controls: 1,
                    playsinline: 1,
                    rel: 0,
                    modestbranding: 1,
                    enablejsapi: 1,
                    mute: shouldAutoPlay ? 1 : 0,
                    origin: window.location.origin,
                },
                events: {
                    onReady: () => {
                        if (cancelled) return;
                        isReadyRef.current = true;
                        syncPlayback();
                    },
                    onStateChange: (event: any) => {
                        if (event.data !== (window as any).YT.PlayerState.ENDED) return;
                        if (!isEditStatus && !isInViewRef.current) return;

                        event.target.seekTo(0, true);
                        event.target.playVideo();
                    },
                },
            });
        };

        initPlayer();

        return () => {
            cancelled = true;
            isReadyRef.current = false;
            playerRef.current?.destroy?.();
            playerRef.current = null;
        };
    }, [isMounted, shouldLoadPlayer, videoId, shouldAutoPlay, isEditStatus]);

    useEffect(() => {
        if (isEditStatus) return;

        const container = containerRef.current;
        if (!container) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries.some((entry) => entry.isIntersecting);
                isInViewRef.current = visible;

                if (visible) {
                    setShouldLoadPlayer(true);
                    if (isReadyRef.current && shouldAutoPlay) {
                        playerRef.current?.playVideo?.();
                    }
                } else if (isReadyRef.current) {
                    playerRef.current?.pauseVideo?.();
                }
            },
            {
                threshold: 0.35,
            },
        );

        observer.observe(container);

        return () => {
            observer.disconnect();
        };
    }, [isEditStatus, shouldAutoPlay, videoId]);

    return (
        <section className={`${styles.content} temp-AutoYoutube temp-hide temp-${id}`} style={{ backgroundColor: bgColor ? bgColor : '' }} id={id} data-section-id="editor-AutoYoutube">
            <DefaultStyle id={id} attr={attr} />
            <div className="AutoYoutube-container">
                <div className="AutoYoutube-video" ref={containerRef}>
                    {isMounted && shouldLoadPlayer && videoId ? (
                        <div className="AutoYoutube-player">
                            <div ref={playerHostRef} key={videoId} />
                        </div>
                    ) : (
                        <div className="AutoYoutube-empty">{videoId ? '' : '请配置 YouTube 视频链接'}</div>
                    )}
                </div>
            </div>
        </section>
    );
}
